import React, { useContext, useState } from "react"
import { observer } from "mobx-react"
import { Button, Group, Text, Title } from "@mantine/core"
import { IconArrowRight } from "@tabler/icons-react"
import { AppWorkspaceContext } from "../../stores/AppWorkspace"
import { FRAMEWORKS, LANDING_COPY } from "../../../docs/catalog"
import type { Framework } from "../../../docs/types"
import { FRAMEWORKS_HASH, integrationPath } from "../../helpers/constants"
import { AgentDirectionsMenu } from "./AgentDirectionsMenu"
import { captureFrameworkSelect } from "../../../analytics"
import { TestedBadge } from "../platforms/TestedBadge"
import { IntegrationTypeBadge } from "./IntegrationTypeBadge"
import { APP_GROUPS, groupOf, STORES, type GroupId } from "../../helpers/frameworkGroups"

// The homepage's "Choose your framework" section: one compact card per
// contract row, grouped the way visitors look for themselves. Someone who runs
// a store or billing panel looks for its name, so every plugin (and every
// store reached through BTCPay) comes first; someone building their own app
// looks for their language (helpers/frameworkGroups, shared with the
// agent-directions modal). Filter chips narrow the grid to one group. Within
// a group, rows keep the contract's order.
//
// A card keeps the name and type, the pitch, the Tested badge, Get started,
// and a "For agents" menu with the two copies. The floor (`requires`) and the
// full copy pair live on each integration page, one click away.

type FilterId = "all" | GroupId

const inGroup = (id: GroupId) => FRAMEWORKS.filter((framework) => groupOf(framework) === id)

const FrameworkCard: React.FC<{ framework: Framework }> = observer(({ framework }) => {
  const workspace = useContext(AppWorkspaceContext)
  const copy_ = LANDING_COPY.frameworks[framework.id]
  const href = integrationPath(framework.id)
  const logo = workspace.frameworkLogoUrl(framework.id)
  const go = (event: React.MouseEvent) => {
    event.preventDefault()
    captureFrameworkSelect(framework.id, "homepage_framework_grid")
    workspace.goIntegration(framework.id)
  }

  return (
    <li className="or-framework-card or-framework-card-compact">
      <a className="or-framework-card-link" href={href} onClick={go}>
        <span className="or-framework-card-head">
          {logo ? (
            <span className="or-framework-logo" data-framework={framework.id}>
              <img src={logo} alt="" />
            </span>
          ) : null}
          <Text className="or-framework-card-title" component="span">
            {framework.label}
          </Text>
          {/* Contract v9's type, beside the name so the Tested badge below
              keeps a row to itself. */}
          <IntegrationTypeBadge type={framework.type} className="or-framework-card-type" />
        </span>
        <Text className="or-framework-card-pitch">{copy_?.pitch ?? framework.heading}</Text>
      </a>
      {/* Contract v8's Tested badge: only a row with a passing live run
          gets one. */}
      {framework.tested ? (
        <div className="or-framework-card-badges">
          <TestedBadge tested={framework.tested} />
        </div>
      ) : null}
      <Group className="or-framework-card-actions" gap="xs" wrap="wrap">
        <Button component="a" href={href} size="sm" rightSection={<IconArrowRight size={16} />} onClick={go}>
          Get started
        </Button>
        {framework.agentStack ? (
          <AgentDirectionsMenu
            stack={framework.agentStack}
            framework={framework.id}
            ctaLocation="homepage_framework_grid"
          />
        ) : null}
      </Group>
    </li>
  )
})

const FrameworkCards: React.FC<{ group: GroupId; labelledBy: string }> = ({ group, labelledBy }) => (
  <ul className="or-framework-cards" aria-labelledby={labelledBy}>
    {inGroup(group).map((framework) => (
      <FrameworkCard key={framework.id} framework={framework} />
    ))}
  </ul>
)

export const FrameworkGrid: React.FC = observer(() => {
  const [filter, setFilter] = useState<FilterId>("all")
  const showStores = filter === "all" || filter === "stores"
  const appGroups = APP_GROUPS.filter((group) => filter === "all" || filter === group.id)
  const chips: { id: FilterId; label: string; count: number }[] = [
    { id: "all" as FilterId, label: "All", count: FRAMEWORKS.length },
    ...[STORES, ...APP_GROUPS].map((group) => ({
      id: group.id as FilterId,
      label: group.chip,
      count: inGroup(group.id).length
    }))
  ].filter((chip) => chip.count > 0)

  return (
    <section id={FRAMEWORKS_HASH} className="or-frameworks" aria-labelledby="or-frameworks-title">
      <Text className="or-eyebrow">Frameworks</Text>
      <Title order={2} id="or-frameworks-title" className="or-frameworks-title">
        Choose your framework
      </Title>

      <div className="or-framework-filters" role="group" aria-label="Show integrations for">
        {chips.map((chip) => (
          <button
            key={chip.id}
            type="button"
            className="or-framework-filter"
            aria-pressed={filter === chip.id}
            onClick={() => setFilter(chip.id)}
          >
            {chip.label}
            <span className="or-framework-filter-count">{chip.count}</span>
          </button>
        ))}
      </div>

      {showStores ? (
        <div className="or-framework-section">
          <Title order={3} id="or-frameworks-stores" className="or-framework-section-title">
            {STORES.heading}
          </Title>
          <Text className="or-framework-section-lede">
            Already run a store or billing panel? Add OpenReceive as its plugin.
          </Text>
          <FrameworkCards group="stores" labelledBy="or-frameworks-stores" />
        </div>
      ) : null}

      {appGroups.length > 0 ? (
        <div className="or-framework-section">
          <Title order={3} className="or-framework-section-title">
            Your own app
          </Title>
          <Text className="or-framework-section-lede">
            Building the app yourself? Add the package for its language.
          </Text>
          {appGroups.map((group) => (
            <div key={group.id} className="or-framework-group">
              <Title order={4} id={`or-frameworks-${group.id}`} className="or-framework-group-title">
                {group.heading}
              </Title>
              <FrameworkCards group={group.id} labelledBy={`or-frameworks-${group.id}`} />
            </div>
          ))}
        </div>
      ) : null}
    </section>
  )
})
